'use client'
import type { UserAccountPortfolio, UserProject } from '@repo/services'
import {
  UserDetailedData,
  useGetProjectCertDetails,
  useUserAuthDetails,
} from '@repo/services'
import { useTranslate } from '@repo/shared/hooks'
import {
  Button,
  Card,
  CenterBox,
  Col,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  Separator,
  Typography,
} from '@repo/ui/components'
import html2canvas from 'html2canvas'
import Image from 'next/image'
import QRCode from 'qrcode'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface CertificateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  project: UserProject
  userProject: UserAccountPortfolio
}

export const CertificateDialog = ({
  open,
  onOpenChange,
  project,
  userProject,
}: CertificateDialogProps) => {
  const { t, tRich } = useTranslate()
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [qrCodeError, setQrCodeError] = useState<string>('')
  const [certificateImageUrl, setCertificateImageUrl] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState<boolean>(false)

  // Fetch user auth details for certificate holder name
  const { data: authDetails } = useUserAuthDetails()

  // Fetch project certificate details including ownership
  const { data: certDetails } = useGetProjectCertDetails(project.token, open)

  // Transform portfolio data to certificate format
  const certificateData = useMemo(() => {
    if (!project || !userProject) return null

    return {
      propertyInfo: {
        certificateHolder:
          `${authDetails?.data?.details?.firstName || ''} ${authDetails?.data?.details?.lastName || ''}`.trim() ||
          '---',
        propertyUsageType: t('filters_usage_' + project.type) || '---',
        address: project.location?.city?.name || '---',
        constructionStatus:
          project.details?.find(
            (x: UserDetailedData) => x.key === 'construction_status',
          )?.value || '---',
        buildingNumber:
          project.details?.find((x) => x.key === 'building_number')?.value ||
          '---',
        owner: project.details?.find((x) => x.key === 'owner')?.value || '---',
        landPlotDetails:
          project.details?.find((x) => x.key === 'land_plot_details')?.value ||
          '---',
        ownershipDeedStatus:
          project.details?.find((x) => x.key === 'ownership_deed_status')
            ?.value || '---',
        floor: project.details?.find((x) => x.key === 'floor')?.value || '---',
        projectName: project.displayName || '---',
        amenities:
          project.details?.find((x) => x.key === 'amenities')?.value || '---',
        country: 'ایران',
        internalArea:
          project.details?.find((x) => x.key === 'internal_area')?.value ||
          '---',
        city: project.location?.city?.name || '---',
        balconyArea:
          project.details?.find((x) => x.key === 'balcony_area')?.value ||
          '---',
        district: project.location?.district?.name || '---',
        totalArea:
          project.details?.find((x) => x.key === 'unit_area')?.value || '---',
        commonAreaShare:
          project.details?.find((x) => x.key === 'common_area_share')?.value ||
          '---',
      },
      tokenInfo: {
        totalTokens:
          (certDetails?.data?.ownership?.shares || 0).toString() || '---',
        tokenValue:
          (
            Number(certDetails?.data?.ownership?.value) /
            Number(certDetails?.data?.ownership?.shares)
          ).toString() || '---',
        totalPropertyValue:
          (certDetails?.data?.ownership?.value || 0).toString() || '---',
        certificateNumber: project.id || '---',
        issuanceDate: new Date().toLocaleDateString('fa-IR') || '--',
      },
    }
  }, [
    project,
    userProject,
    certDetails?.data?.ownership?.shares,
    certDetails?.data?.ownership?.value,
    authDetails?.data?.details?.firstName,
    authDetails?.data?.details?.lastName,
    t,
  ])

  const formatNumber = (num: number) => {
    return num.toLocaleString('fa-IR')
  }

  // PNG generation
  const generatePNG = useCallback(async () => {
    try {
      setIsGenerating(true)
      const element = document.getElementById('certificate-container')
      if (!element) {
        throw new Error('Certificate container not found')
      }
      const alertContainer = document.getElementById('alert-container')
      if (alertContainer) {
        alertContainer.style.paddingTop = '0px'
        alertContainer.style.marginTop = '30px'
      }

      const titleContainer = document.getElementById('title-container')
      if (titleContainer) {
        titleContainer.style.marginTop = '10px'
      }

      const mainContainer = document.getElementById('certificate-main')
      if (mainContainer) {
        mainContainer.style.width = '1446px'
        mainContainer.style.margin = '10px'
      }

      // Convert images to base64 to avoid CORS issues - only check images within the dialog
      const dialogElement = element.querySelector('[role="dialog"]') || element
      const images = dialogElement.querySelectorAll('img')

      for (const img of images) {
        if (img.src && !img.src.startsWith('data:')) {
          img.style.marginBottom = '0px'
          try {
            const response = await fetch(img.src, {
              mode: 'cors',
              cache: 'no-cache',
            })

            if (!response.ok) {
              continue
            }

            const blob = await response.blob()
            const reader = new FileReader()
            const base64 = await new Promise<string>((resolve, reject) => {
              reader.onload = () => resolve(reader.result as string)
              reader.onerror = () => reject(reader.error)
              reader.readAsDataURL(blob)
            })
            img.src = base64
          } catch (error) {
            console.log('Failed to convert image to base64:', error)
            // Continue with original image
          }
        } else {
          console.log('Skipping image (already base64 or no src):', img.src)
        }
      }

      // Wait for all images to load
      await Promise.all(
        Array.from(images).map((img) => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve(true)
            } else {
              // Set timeout for mobile
              const timeout = setTimeout(() => {
                resolve(true)
              }, 5000) // 5 second timeout

              img.onload = () => {
                clearTimeout(timeout)
                resolve(true)
              }

              img.onerror = () => {
                clearTimeout(timeout)
                resolve(true) // Continue even if image fails
              }
            }
          })
        }),
      )
      // Add a small delay to ensure all images are rendered
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Create canvas with images - now that images are base64, CORS should work
      const canvas = await html2canvas(element, {
        scale: 1,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#DBD3E5',
        logging: false,
        imageTimeout: 15000,
        removeContainer: true,
      })

      // Reset container width after capture
      if (mainContainer) {
        mainContainer.style.width = 'auto'
      }

      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/png', 1)
      setCertificateImageUrl(imgData)
      setIsGenerating(false)
    } catch (error) {
      console.error('PNG generation error:', error)
      setIsGenerating(false)
    }
  }, [])

  // Download PNG function
  const downloadPNG = useCallback(() => {
    if (!certificateImageUrl) return

    const link = document.createElement('a')
    link.download = `certificate-${new Date().toLocaleDateString('fa-IR').replace(/\//g, '-')}.png`
    link.href = certificateImageUrl
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [certificateImageUrl])

  // Generate QR code based on current page URL
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        // Get the current page URL
        const currentUrl = window.location.href

        // Generate QR code as data URL
        const qrDataUrl = await QRCode.toDataURL(currentUrl, {
          width: 160,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })

        setQrCodeDataUrl(qrDataUrl)
        setQrCodeError('')
      } catch (error) {
        console.error('Error generating QR code:', error)
        setQrCodeError('Failed to generate QR code')
      }
    }

    // Only generate QR code on client side
    if (typeof window !== 'undefined') {
      generateQRCode()
    }
  }, [])

  // Auto-generate PNG when dialog opens
  useEffect(() => {
    if (open && project && userProject && certificateData) {
      // Add a small delay to ensure the component is fully rendered
      const timer = setTimeout(() => {
        generatePNG()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [open, project, userProject, certificateData, generatePNG])

  if (!certificateData) {
    return null
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        className={
          isGenerating
            ? 'min-h-[650px]'
            : 'px-0 pb-md max-h-[calc(100dvh_-_192px)]'
        }
      >
        <ModalHeader>
          <ModalTitle></ModalTitle>
        </ModalHeader>
        {/* Loading indicator or generated image */}
        {isGenerating ? (
          <div className='fixed rounded-4xl inset-0 bg-neutral-surface-light rounded-t-lg flex items-center justify-center z-50 h-full'>
            <div className='bg-neutral-surface-5 p-2xl rounded-lg text-center'>
              <Typography variant='p' className='text-lg'>
                {t('certificate_generating_image')}
              </Typography>
            </div>
          </div>
        ) : certificateImageUrl ? (
          <div className='pt-0 sm:pb-0 bg-neutral-surface-light inset-0 text-center w-full pb-3xl max-h-[calc(100dvh_-_252px)] overflow-auto'>
            <div className='justify-center bg-neutral-surface-light px-2xl pb-xs hidden sm:flex absolute left-0 top-sm w-full'>
              <Button size='xs' variant='ghost' onClick={downloadPNG}>
                <Typography variant='p' className='text-lg font-bold px-xs'>
                  {t('certificate_download_image')}
                </Typography>
              </Button>
            </div>
            <div className='bg-neutral-surface-light sm:px-md text-center w-full overflow-y-auto max-h-full'>
              <Image
                src={certificateImageUrl}
                alt='Certificate'
                width={800}
                height={600}
                className='w-full h-auto'
              />
            </div>
            <div className='px-md flex sm:hidden absolute bottom-md left-0 right-0'>
              <Button
                size='xs'
                variant='ghost'
                width='full'
                onClick={downloadPNG}
              >
                <Typography variant='p' className='text-lg font-bold'>
                  {t('certificate_download_image')}
                </Typography>
              </Button>
            </div>
          </div>
        ) : null}

        <div
          id='certificate-main'
          className='p-lg !text-[#312640] fixed top-[300%] left-[300%] -z-[999]'
        >
          <div className='max-w-container-xl mx-auto'>
            <Card
              id='certificate-container'
              className='bg-[#DBD3E5] rounded-none'
              style={{
                backgroundColor: '#ffffff',
                borderColor: '#ffffff',
              }}
            >
              <Card
                className='bg-[#DBD3E5] rounded-none m-10'
                style={{
                  backgroundImage: 'url(/vector-pattern.png)',
                  backgroundRepeat: 'repeat',
                  backgroundPosition: 'bottom left',
                  border: '4px solid #9066CC',
                }}
              >
                {/* Header */}
                <div className='text-center p-2xl'>
                  <Image
                    alt='logo'
                    src='/fav/android-chrome-192x192.png'
                    width={80}
                    height={80}
                    className='m-auto'
                  />
                  <Image
                    alt='logo'
                    src='/logo-text.svg'
                    width={125}
                    height={56}
                    className='object-contain max-w-[125px] mt-sm m-auto'
                  />
                  <div
                    id='title-container'
                    style={{
                      marginTop: '72px',
                      paddingBottom: '16px',
                    }}
                  >
                    <Typography
                      variant='p'
                      className='text-[52px] !font-bold !text-[#36264D] !font-abar'
                    >
                      {t('certificate_title')}
                    </Typography>
                  </div>
                  <div
                    id='alert-container'
                    style={{
                      backgroundColor: '#ffffff40',
                      border: '1px solid #9066CC',
                      padding: '20px',
                    }}
                    className='!rounded-lg'
                  >
                    <span className='text-[18px] !text-[#312640] !font-abar text-center'>
                      {tRich.rich('certificate_alert', {
                        date: new Date().toLocaleDateString('fa-IR'),
                      })}
                    </span>
                  </div>
                </div>

                <div className='p-2xl'>
                  <div className='mb-md'>
                    <div className='mb-md'>
                      <Typography
                        variant='p'
                        className='!font-abar-low text-[18px] !font-bold'
                      >
                        {t('certificate_property_info')}
                      </Typography>
                    </div>

                    <div className='grid grid-cols-2 gap-x-6xl gap-y-xs'>
                      {/* certificateHolder */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_holder')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.certificateHolder}
                        </Typography>
                      </div>

                      {/* district */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_district')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.district}
                        </Typography>
                      </div>

                      {/* propertyUsageType */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_property_usage_type')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.propertyUsageType}
                        </Typography>
                      </div>

                      {/* address */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_address')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.address}
                        </Typography>
                      </div>

                      {/* projectName */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_project_name')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.projectName}
                        </Typography>
                      </div>

                      {/* floor */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_floor')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.floor}
                        </Typography>
                      </div>

                      {/* country */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_country')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.country}
                        </Typography>
                      </div>

                      {/* totalArea */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_total_area')}
                        </Typography>

                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.totalArea}{' '}
                          <span className='!font-regular !font-abar-low opacity-50'>
                            {tRich.rich('common_meter_square', {
                              value: () => '',
                            })}
                          </span>
                        </Typography>
                      </div>

                      {/* city */}
                      <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_city')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.city}
                        </Typography>
                      </div>

                      {/* buildingNumber */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_building_number')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.buildingNumber}
                        </Typography>
                      </div> */}

                      {/* constructionStatus */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_construction_status')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.constructionStatus}
                        </Typography>
                      </div> */}

                      {/* owner */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_owner')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.owner}
                        </Typography>
                      </div> */}

                      {/* landPlotDetails */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_land_plot_details')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.landPlotDetails}
                        </Typography>
                      </div> */}

                      {/* ownershipDeedStatus */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_ownership_deed_status')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.ownershipDeedStatus}
                        </Typography>
                      </div> */}

                      {/* internalArea */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_internal_area')}
                        </Typography>

                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.internalArea}
                        </Typography>
                      </div> */}

                      {/* balconyArea */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_balcony_area')}
                        </Typography>

                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.balconyArea}{' '}
                          <span className='!font-regular !font-abar-low opacity-50'>
                            {tRich.rich('common_meter_square', {
                              value: () => '',
                            })}
                          </span>
                        </Typography>
                      </div> */}

                      {/* commonAreaShare */}
                      {/* <div className='mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='text-[20px] !font-abar-low'
                        >
                          {t('certificate_common_area_share')}
                        </Typography>
                        <Typography
                          variant='p'
                          className='!font-abar-low text-[20px] !font-bold'
                        >
                          {certificateData.propertyInfo.commonAreaShare}{' '}
                          <span className='!font-regular !font-abar-low opacity-50'>
                            {tRich.rich('common_meter_square', {
                              value: () => '',
                            })}
                          </span>
                        </Typography>
                      </div> */}
                    </div>
                  </div>
                  <Separator className='bg-neutral-surface-20' />
                  <div className='pt-md pb-2xl'>
                    {/* amenities */}
                    <div className='flex gap-sm'>
                      <Typography
                        variant='p'
                        className='text-[20px] !font-abar-low'
                      >
                        {t('certificate_amenities')}
                      </Typography>
                      <Typography
                        variant='p'
                        className='!font-abar-low text-[20px] !font-bold'
                      >
                        {certificateData.propertyInfo.amenities}
                      </Typography>
                    </div>
                  </div>

                  {/* Token Information Section */}
                  <div
                    style={{
                      backgroundColor: '#ffffff40',
                    }}
                    className='!mb-md !p-lg !rounded-lg !mt-2xl'
                  >
                    <Typography
                      variant='p'
                      className=' text-2xl !font-abar-low !font-bold text-[18px] mb-lg pb-sm'
                    >
                      {t('certificate_token_info')}
                    </Typography>

                    <div className='  grid grid-cols-1 md:grid-cols-1 gap-xs'>
                      <div className=' mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='  text-[20px] !font-abar-low'
                        >
                          {t('certificate_total_tokens')}
                        </Typography>
                        <Typography
                          variant='p'
                          className=' text-[20px] !font-abar-low !font-bold'
                        >
                          {certificateData.tokenInfo.totalTokens !== '---' &&
                            formatNumber(
                              Number(certificateData.tokenInfo.totalTokens),
                            )}
                        </Typography>
                      </div>

                      <div className='  mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className='  text-[20px] !font-abar-low'
                        >
                          {tRich.rich('certificate_token_value', {
                            time: (time) => (
                              <Typography
                                color='text-neutral-surface-60'
                                className=''
                              >
                                {time}
                              </Typography>
                            ),
                          })}
                        </Typography>
                        <Typography
                          variant='p'
                          className=' text-[20px] !font-abar-low !font-bold'
                        >
                          {certificateData.tokenInfo.tokenValue !== '---' &&
                            formatNumber(
                              Number(certificateData.tokenInfo.tokenValue),
                            )}{' '}
                          <span className='!font-regular opacity-50'>ریال</span>
                        </Typography>
                      </div>

                      <div className=' mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className=' text-[20px] !font-abar-low'
                        >
                          {t('certificate_total_property_value')}
                        </Typography>
                        <Typography
                          variant='p'
                          className=' text-[20px] !font-abar-low !font-bold'
                        >
                          {certificateData.tokenInfo.totalPropertyValue !==
                            '---' &&
                            formatNumber(
                              Number(
                                certificateData.tokenInfo.totalPropertyValue,
                              ),
                            )}{' '}
                          <span className='!font-regular opacity-50'>ریال</span>
                        </Typography>
                      </div>

                      <div className=' mb-md flex justify-between'>
                        <Typography
                          variant='p'
                          className=' text-[20px] !font-abar-low'
                        >
                          {t('certificate_issuance_date')}
                        </Typography>
                        <Typography
                          variant='p'
                          className=' text-[20px] !font-abar-low !font-bold'
                        >
                          {certificateData.tokenInfo.issuanceDate}
                        </Typography>
                      </div>
                    </div>
                  </div>

                  {/* Disclaimers Section */}
                  <Col className='mb-md p-lg gap-y-md text-center'>
                    <Typography
                      variant='p'
                      className='leading-relaxed text-[16px] !font-abar-low'
                    >
                      {t('certificate_disclaimer_1')}
                    </Typography>
                    <Typography
                      variant='p'
                      className='leading-relaxed text-[16px] !font-abar-low'
                    >
                      {t('certificate_disclaimer_2')}
                    </Typography>
                    <Typography
                      variant='p'
                      className='leading-relaxed text-[16px] !font-abar-low'
                    >
                      {t('certificate_disclaimer_3')}
                    </Typography>
                    <Typography
                      variant='p'
                      className='leading-relaxed text-[16px] !font-abar-low'
                    >
                      {t('certificate_disclaimer_4')}
                    </Typography>
                    <Typography
                      variant='p'
                      className='leading-relaxed text-[16px] !font-abar-low'
                    >
                      {t('certificate_disclaimer_5')}
                    </Typography>
                  </Col>

                  {/* QR Code and Identifier */}
                  <div className='text-center'>
                    <div className='inline-block bg-white p-xs rounded-lg shadow-md mb-md'>
                      <CenterBox className='w-[160px] h-[160px]'>
                        {qrCodeDataUrl ? (
                          <Image
                            src={qrCodeDataUrl}
                            alt='qr-code'
                            width={160}
                            height={160}
                            className='opacity-0'
                          />
                        ) : qrCodeError ? (
                          <div className='w-[160px] h-[160px] flex items-center justify-center bg-gray-100 text-gray-500 text-xs text-center p-2'>
                            {qrCodeError}
                          </div>
                        ) : (
                          <div className='w-[160px] h-[160px] flex items-center justify-center bg-gray-100 text-gray-500 text-xs'>
                            Loading QR Code...
                          </div>
                        )}
                      </CenterBox>
                    </div>
                    <Typography
                      variant='p'
                      className='opacity-0 font-mono text-sm !text-[#475950]'
                    >
                      3f5b7e9a1c2d4e6f8a9b0c1d2e3f4a5b6c7d8e9f0
                    </Typography>
                  </div>
                </div>
              </Card>
            </Card>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
